import { useState } from 'react';
import { Link } from 'react-router';
import { GetStarted, useGetStarted } from '../components/GetStarted';
import { DemoBadge, Empty, ErrorMsg, Loading, PageHead, Section, Sq, StatusMark, Time, TzNote } from '../components/ui';
import { ACTION_LABEL, explainJobError, JOB_TYPE_LABEL, jobType, plural } from '../lib/format';
import { useApi } from '../lib/session';
import type { Overview as OverviewData } from '../lib/types';

function changeText(c: OverviewData['changes'][number]): string {
  const d = c.detail || {};
  if (c.action === 'studio.create' && typeof d.domain === 'string') return d.domain;
  if (c.action === 'import.run') {
    const n = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : 0);
    return `${n('rows')} rows: ${n('created')} created, ${n('matched')} matched, ${n('suppressed')} suppressed, ${n('invalid')} invalid`;
  }
  if (c.action === 'studio.promote' && Array.isArray(d.channels)) return `channels: ${(d.channels as string[]).join(', ')}`;
  if (c.action === 'fact.accept' && typeof d.field === 'string') return d.field;
  return '';
}

function changeLink(c: OverviewData['changes'][number]): string | null {
  if (!c.entity_id) return null;
  if (c.action === 'import.run') return `/competitors?batch_id=${c.entity_id}&include_dismissed=1`;
  if (c.action.startsWith('studio.') || c.action.startsWith('fact.')) return `/competitors/${c.entity_id}`;
  return null;
}

const CHANGES_SHOWN = 6;
const NO_SETUP = { worker: true, studios: true, review: true, deep: true, quotation: true, brand: true, prospect: true };

export function Overview() {
  const { data, error, loading } = useApi<OverviewData>('/api/overview');
  const [allChanges, setAllChanges] = useState(false);
  const setup = useGetStarted(data?.setup ?? NO_SETUP);

  if (loading && !data) return <Loading what="Loading the overview" />;
  if (!data) return <ErrorMsg>{error || 'The overview could not be loaded.'}</ErrorMsg>;
  const nr = data.needs_review;
  const dir = data.directory;
  const fr = data.freshness;
  const nothingToReview =
    !nr.unreviewed_studios && !nr.unreviewed_demo_studios && !nr.proposed_facts && !nr.batches.length && !nr.jobs_waiting_review && !nr.quotations_to_review;
  const ranTypes = Object.keys(JOB_TYPE_LABEL).filter((t) => fr.last_success_by_job_type[t]);
  const notRunTypes = Object.keys(JOB_TYPE_LABEL).filter((t) => !fr.last_success_by_job_type[t]);
  const changes = allChanges ? data.changes : data.changes.slice(0, CHANGES_SHOWN);

  return (
    <div className="page">
      <PageHead title="Overview" meta={<>Generated <Time iso={data.generated_at} /> · <TzNote /></>}>
        {setup.canReopen && (
          <button type="button" className="btn small" onClick={setup.show}>
            Get started ({setup.done} of {setup.total})
          </button>
        )}
      </PageHead>
      {error && <ErrorMsg>{error}</ErrorMsg>}

      {setup.visible && <GetStarted state={data.setup} onHide={setup.hide} />}

      <div className="overview-grid">
        <Section title="Needs your review">
          {nothingToReview ? (
            <Empty>Nothing is waiting for review.</Empty>
          ) : (
            <ul className="lines">
              <li>
                <span className="num big">{nr.unreviewed_studios}</span>
                <span>
                  real {nr.unreviewed_studios === 1 ? 'studio' : 'studios'} not reviewed yet{' '}
                  {nr.unreviewed_studios > 0 && <Link to="/competitors?interest=unreviewed&demo=hide">review</Link>}
                </span>
              </li>
              {nr.unreviewed_demo_studios > 0 && (
                <li>
                  <span className="num big">{nr.unreviewed_demo_studios}</span>
                  <span>
                    <DemoBadge /> demo studios not reviewed (fictional, counted apart){' '}
                    <Link to="/competitors?interest=unreviewed&demo=only">see them</Link>
                  </span>
                </li>
              )}
              <li>
                <span className="num big">{nr.proposed_facts}</span>
                <span>
                  proposed {nr.proposed_facts === 1 ? 'fact' : 'facts'} from website collection waiting for accept / reject
                  {nr.proposed_facts > 0 && <span className="muted"> (on each studio’s Overview tab)</span>}
                </span>
              </li>
              {(nr.quotations_to_review ?? 0) > 0 && (
                <li>
                  <span className="num big">{nr.quotations_to_review}</span>
                  <span>
                    {nr.quotations_to_review === 1 ? 'quotation' : 'quotations'} to review{' '}
                    <Link to="/pricing">open Pricing</Link>
                  </span>
                </li>
              )}
              <li>
                <span className="num big">{nr.jobs_waiting_review}</span>
                <span>
                  {nr.jobs_waiting_review === 1 ? 'job' : 'jobs'} waiting for review{' '}
                  {nr.jobs_waiting_review > 0 && <Link to="/jobs?status=waiting_review">open</Link>}
                </span>
              </li>
            </ul>
          )}
          {nr.batches.length > 0 && (
            <>
              <h3 className="sub">Batches not marked reviewed</h3>
              <ul className="rows">
                {nr.batches.map((b) => (
                  <li key={b.id}>
                    <Link to={`/competitors?batch_id=${b.id}&include_dismissed=1`}>{b.label}</Link> {b.is_demo && <DemoBadge />}
                    <span className="muted small">
                      {' '}
                      · {b.kind} · <span className="num">{String(b.counts.created ?? 0)}</span> created · <Time iso={b.created_at} />
                    </span>
                  </li>
                ))}
              </ul>
              <p className="small">
                <Link to="/jobs#batches">Mark batches reviewed on the Jobs page</Link>
              </p>
            </>
          )}
        </Section>

        <Section title="Freshness" aside={<Link to="/jobs">Jobs</Link>}>
          <p className="worker-line">
            <StatusMark status={fr.worker_alive ? (fr.worker_state === 'running' ? 'running' : fr.worker_state || 'unknown') : 'failed'} />{' '}
            {fr.worker_seen_at ? (
              <>
                Worker {fr.worker_alive ? 'alive' : 'not responding'} · state {fr.worker_state ?? 'unknown'} · last seen <Time iso={fr.worker_seen_at} relative />
              </>
            ) : (
              <>No worker has ever reported in. Jobs will wait in the queue until one runs.</>
            )}
          </p>
          {ranTypes.length > 0 && (
            <table className="table compact">
              <thead>
                <tr>
                  <th>Job type</th>
                  <th>Last success</th>
                </tr>
              </thead>
              <tbody>
                {ranTypes.map((t) => (
                  <tr key={t}>
                    <td>{jobType(t)}</td>
                    <td>
                      <Time iso={fr.last_success_by_job_type[t]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {notRunTypes.length > 0 && (
            <p className="muted small">
              {ranTypes.length ? 'Never succeeded yet: ' : 'No job has succeeded yet. Job types: '}
              {notRunTypes.map(jobType).join(', ')}.
            </p>
          )}
        </Section>

        <Section title="Failures and jobs needing attention" aside={<span className="muted small">last 7 days</span>}>
          {data.attention.length === 0 ? (
            <Empty>No failed jobs in the last 7 days, and none flagged for attention.</Empty>
          ) : (
            <ul className="rows">
              {data.attention.map((j) => (
                <li key={j.id}>
                  <StatusMark status={j.status} /> <Link to={`/jobs/${j.id}`}>{jobType(j.type)}</Link>
                  {j.studio_name && <span> · {j.studio_name}</span>}
                  <span className="muted small"> · <Time iso={j.finished_at} /></span>
                  {j.error &&
                    (explainJobError(j.error) ? (
                      <details className="small fold">
                        <summary className="err-text">{explainJobError(j.error)}</summary>
                        <p className="mono muted">{j.error}</p>
                      </details>
                    ) : (
                      <p className="small err-text">{j.error}</p>
                    ))}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="What changed" aside={<span className="muted small">last 7 days</span>}>
          <ul className="facts-line small">
            <li>
              <span className="num">{data.changed_pages_this_week}</span> website pages changed{' '}
              <span className="muted">(only pages collected twice or more count)</span>
            </li>
            <li>
              <span className="num">{data.material_changes_unseen ?? 0}</span> material changes not reviewed{' '}
              <span className="muted">(each Deep studio’s Changes tab)</span>
            </li>
            <li>
              <span className="num">{data.prospects?.follow_ups_due ?? 0}</span> prospect follow-ups due,{' '}
              <span className="num">{data.prospects?.drafts_to_approve ?? 0}</span> drafts to approve · <Link to="/prospects?due=1">Prospects</Link>
            </li>
            <li>
              {data.latest_report ? (
                <>
                  Latest weekly report: <Link to={`/reports/${data.latest_report.id}`}>week of {data.latest_report.period_start}</Link> ({data.latest_report.status})
                </>
              ) : (
                <>
                  No weekly report yet · <Link to="/reports">Reports</Link>
                </>
              )}
            </li>
          </ul>
          {data.changes.length === 0 ? (
            <Empty>No studios added, merged, promoted or reviewed in the last 7 days.</Empty>
          ) : (
            <ul className="rows">
              {changes.map((c, i) => {
                const href = changeLink(c);
                const label = ACTION_LABEL[c.action] ?? c.action;
                return (
                  <li key={i}>
                    <span className="muted small num">
                      <Time iso={c.at} />
                    </span>{' '}
                    {href ? <Link to={href}>{label}</Link> : label}
                    {changeText(c) && <span className="muted"> · {changeText(c)}</span>}
                    <span className="muted small"> · by {c.actor.replace(/^user:/, '')}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {data.changes.length > CHANGES_SHOWN && (
            <button type="button" className="link small" onClick={() => setAllChanges((v) => !v)} aria-expanded={allChanges}>
              {allChanges ? 'Show fewer' : `Show all ${data.changes.length}`}
            </button>
          )}
        </Section>

        <Section title="What runs next">
          {data.next_runs.length === 0 ? (
            <Empty>Nothing is scheduled. Scheduled collection starts when a studio is promoted to Deep.</Empty>
          ) : (
            <ul className="rows">
              {data.next_runs.map((r) => (
                <li key={r.schedule_id}>
                  <span className="num">
                    <Time iso={r.next_run_at} />
                  </span>{' '}
                  · {jobType(r.task_type)} ({r.channel || 'all'}){' '}
                  {r.studio_id ? <Link to={`/competitors/${r.studio_id}`}>{r.studio_name ?? 'studio'}</Link> : null}
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title="Content ready">
          {data.content_ready === null ? (
            <Empty>
              Content Studio is not built yet (phase 3), so nothing is reported here. <Link to="/content">What it will do</Link>
            </Empty>
          ) : (
            <p>
              <span className="num big">{data.content_ready}</span> {data.content_ready === 1 ? 'item' : 'items'} ready: approved to export, or exported and not yet marked published.{' '}
              <Link to="/content">Content Studio</Link>
            </p>
          )}
        </Section>

        <Section title="Directory" aside={<Link to="/competitors">Competitors</Link>}>
          <ul className="lines">
            <li>
              <span className="num big">{dir.studios}</span>
              <span>real {dir.studios === 1 ? 'studio' : 'studios'} in the directory</span>
            </li>
            <li>
              <span className="num big">{dir.demo_studios}</span>
              <span>
                <DemoBadge /> demo studios (fictional, kept apart from the real count)
              </span>
            </li>
            <li>
              <span className="num big">{dir.deep}</span>
              <span>followed in Deep mode</span>
            </li>
            <li>
              <span className="num big">{dir.dismissed}</span>
              <span>dismissed</span>
            </li>
            <li>
              <span className="num big">{dir.unmapped}</span>
              <span>real {dir.unmapped === 1 ? 'studio' : 'studios'} without a geocoded city (list only, not on the map)</span>
            </li>
          </ul>
          <p className="muted small">
            <Sq kind="outline" /> The directory holds what has been entered, imported or discovered. It is not a census of any country.
            {dir.studios === 0 && ` No real studio has been added yet: ${plural(dir.demo_studios, 'demo record')} only.`}
          </p>
        </Section>
      </div>
    </div>
  );
}
